// Angular
import { Router } from "@angular/router";
import { Injectable } from "@angular/core";

// Ngxs
import { State, Action, StateContext, Selector, Store } from "@ngxs/store";

// Models
import { IntakeModel } from "app/_models/intake.model";
import { PolicyControl } from "app/_models/policycontrol.model";
import { IntakeStepModel } from "app/_models/intakestep.model";
import { CaseModel, UserProfile } from "app/_models";
import { TravelDetailModel } from "app/_models/travel-detail.model";
import { HelpPlanDetailsModel } from "app/_models/helpPlanDetails.model";
import { intakeTimeValueModel } from "app/_models/intakeTimeValue.model";
import { Insuree } from "app/_models/insuree.model";
import { FellowTraveller } from "app/_models/fellow-traveller.model";
import { PersonalInformation } from "app/_models/personal-information.model";

// Actions
import {
  SetPolicy,
  SetCaseDetail,
  SetHelpPlan,
  SetInsuree,
  SetStartIntakeTime,
  SetTravel,
  SetParty,
  UpdateSetps,
  ValidateHelpPlan,
  SaveStore,
  SetDossier,
  CopyDossier,
  ResetState,
  SetSummary,
  SetNotes,
  SetRehospitalisation,
  SetAdl,
  SetDossierStatus,
  SetDbc,
  SetWorkingOrderLine,
  SetLocations,
  SetCaseType,
  SetPureCloudInfo,
  SetSteps,
  SetDraftDossier,
  SetDraftDossierNumber,
  SetIsUrgent,
  SetActiveStepsPolicy,
  SetInsureeData,
  SetLinkedDossier
} from "app/_actions/intake.actions";

// class-trans
import {
  plainToClass,
} from "class-transformer";

//Mapper
import { Mapper } from "@nartc/automapper";

import { PolicyDto } from "app/_mapper/policy.dto";
import { PolicyVm } from "app/_mapper/policy.vm";

import { InsureeVm } from "app/_mapper/insuree.vm";
import { InsureeDto } from "app/_mapper/insuree.dto";

import "reflect-metadata";
import { CoronerModel } from "app/_models/coroner.model";
import { IntakeDataModel } from "app/_models/intakedata.model";
import { SummaryDetailsModel } from "app/_models/summary-details.model";
import { IntakeMapperService } from "app/intake/intake-mapper.service";
import { DossierNoteModel } from "app/intake/note/models/dossier-note.model";
import { RehospitalisationModel } from "app/intake/rehospitalisation/models/rehospitalisation.model";
import { AdlModel } from "app/intake/adl/models/adl.model";
import { DbcModel } from "app/intake/dbc/models/dbc.model";
import { AuthenticationService, SharedService } from "app/_services";
import { ROLES } from "app/_constants/roles";
import { LocationType } from "app/_models/location-type.model";
import { FUNCTIONALITIES } from "app/_constants/functionalities";
import { StepService } from "app/_services/step.service";
import { STEP } from "app/_constants/steps";
import { PersonalInfo } from "app/_models/PersonalInfo.model";

const INTAKE_STORE_NAME = "intakeStore";

@State<IntakeModel>({
  name: INTAKE_STORE_NAME,
  defaults: new IntakeModel(),
})
@Injectable()
export class IntakeState {
  constructor(private router: Router, private store: Store, private intakeMapperService: IntakeMapperService, private authenticationService:AuthenticationService, private sharedService: SharedService, private stepsService: StepService) { }

  @Selector()
  static getInsuree(state: IntakeModel): Insuree {
    let insuree = state.intake.insuree;
    insuree = plainToClass(Insuree, insuree);

    return insuree;
  }

  @Selector()
  static getTravel(state: IntakeModel): TravelDetailModel {
    let travels = state.intake.travel;
    return travels;
  } 

  @Selector()
  static getEditMode(state: IntakeModel): boolean {
    return state.editMode;
  }

  @Selector()
  static getParty(state: IntakeModel): FellowTraveller[] {
    return state.intake.parties;
  }

  @Selector()
  static getPolicy(state: IntakeModel): PolicyControl {
    let policy = state.intake.policy;

    policy.personalInformations = plainToClass(
      PersonalInfo,
      policy.personalInformations
    );

    return policy;
  }

  @Selector()
  static getStartIntakeTime(state: IntakeModel): intakeTimeValueModel {
    return state.intakeDuration;
  }

  @Selector()
  static getDraftDossierNumber(state: IntakeModel): string {
    return state.draftDossierNumber;
  }

  @Selector()
  static getCreationDate(state: IntakeModel): string {
    return state.creationDate;
  }

  @Selector()
  static getDossierStatus(state: IntakeModel): string {
    return state.intake.dossierStatus;
  }

  @Selector()
  static getCaseDetail(state: IntakeModel): CaseModel {
    return state.intake.case;
  }

  @Selector()
  static getTravelDetail(state: IntakeModel): TravelDetailModel {
    return state.intake.travel;
  }

  @Selector()
  static getDossier(state: IntakeModel): IntakeDataModel {
    return state.intake;
  }

  @Selector()
  static getHelpPlan(state: IntakeModel): HelpPlanDetailsModel[] {
    return state.intake.helpPlans;
  }

  @Selector()
  static getSteps(state: IntakeModel) {
    return state.steps.filter((step) => step.enabled == true);
  }

  @Action(ResetState)
  resetState(ctx: StateContext<IntakeModel>) {
    let state = new IntakeModel();
    state.steps[3].enabled = false;
    state.steps[3].isActive = false;
    state.steps[4].enabled = false;
    state.steps[4].isActive = false;
    state.steps[9].enabled = false;
    state.steps[9].isActive = false;
    state.steps[10].enabled = false;
    state.steps[10].isActive = false;
    state.steps[16].enabled = false;
    state.steps[16].isActive = false;
    state.steps[11].enabled = false;
    state.steps[11].isActive = false;    
    state.steps[12].enabled = false;
    state.steps[12].isActive = false;   
    state.steps[13].enabled = false;
    state.steps[13].isActive = false;   
    state.steps[14].enabled = false;
    state.steps[14].isActive = false;  
    state.steps[15].enabled = false;
    state.steps[15].isActive = false;  
    state.steps.map((step) => {
      step.isActive = step.label == "Policy";
    });
    let intakeDuration = new intakeTimeValueModel();
    intakeDuration.startDate = Date.now();
    state.intakeDuration = intakeDuration;
    state.draftDossierNumber = null;

    ctx.patchState({
      ...state
    });

    this.router.navigate(["/dashboard/home/intake/policy"]);
  }

  @Selector()
  static getIntake(state: IntakeModel) {
    return state;
  }
  @Selector()
  static getSummaryDetail(state: IntakeModel): SummaryDetailsModel {
    return state.intake.summary;
  }

  @Selector()
  static getNotes(state: IntakeModel): DossierNoteModel {
    return state.intake.notes;
  }

  @Selector()
  static getRehospitalisation(state: IntakeModel): RehospitalisationModel {
    return state.intake.rehospitalisation;
  }

  @Selector()
  static getAdl(state: IntakeModel): AdlModel {
    return state.intake.adl;
  }

  @Selector()
  static getLocations(state: IntakeModel): LocationType[] {
    return state.intake.policy.cause.locations;
  }

  @Action(SetSteps)
  setSteps(ctx: StateContext<IntakeModel>, { steps }) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      steps
    });
  }
  @Action(SetSummary)
  SetSummary(ctx: StateContext<IntakeModel>, { summary : summary }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.summary = summary;
    ctx.patchState({
      ...state,
      intake: intake,
    });
  }

  @Action(SetDossierStatus)
  setDossierStatus(ctx: StateContext<IntakeModel>, { dossierStatus }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.dossierStatus = dossierStatus;

    ctx.patchState({
      ...state,
      intake: intake,
    });
  }
  
  @Action(SetNotes)
  setNotes(ctx: StateContext<IntakeModel>, { notes }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.notes = notes;

    ctx.patchState({
      ...state,
      intake: intake,
    });
  }

  @Action(SetRehospitalisation)
  setRehospitalisation(ctx: StateContext<IntakeModel>, { rehospitalisation }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.rehospitalisation = rehospitalisation;

    ctx.patchState({
      ...state,
      intake: intake,
    });
  }

  @Action(SetAdl)
  setAdl(ctx: StateContext<IntakeModel>, { adl }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.adl = adl;

    ctx.patchState({
      ...state,
      intake: intake,
    });
  }

  @Action(CopyDossier)
  async copyDossier(ctx: StateContext<IntakeModel>, {intake: intake, editMode: editMode}){
    let state = new IntakeModel();     

    let _steps = this.getCaseTypeSteps(intake.intake.policy.cause,state)
    _steps = await this.stepsService.getStepswithMandatoryupdated( intake.intake.policy.cause,_steps);

    state.steps = _steps;
    state.steps.map((step) => {
      step.isActive = step.label == "Policy";
    });

    let intakeDuration = new intakeTimeValueModel();
    intakeDuration.startDate = Date.now();
    state.intakeDuration = intakeDuration;

    state.intake = intake.intake;

    ctx.patchState({
      ...state
    });
    this.router.navigate(["/dashboard/home/intake/policy"]);
  }
  @Action(SetDraftDossier)
  setDraftDossier(ctx: StateContext<IntakeModel>, {draftDossier }) {
    ctx.setState({
      ...draftDossier
    });
    this.router.navigate(["/dashboard/home/intake/policy"]);
  }
  @Action(SetDossier)
  setDossier(ctx: StateContext<IntakeModel>, { dossier: dossier, editMode: editMode }) {
    const state = ctx.getState();
    state.editMode = editMode;
    let viewCostControl = this.costControlPermission();
    let _intake = this.intakeMapperService.dataFromApi(dossier);
    _intake.steps[4].enabled = true;
    _intake.steps[4].isActive = true;
    _intake.steps[9].enabled = true;
    _intake.steps[9].isActive = true;
    _intake.steps[10].enabled = true;
    _intake.steps[10].isActive = true;
    _intake.steps[16].enabled = true;
    _intake.steps[16].isActive = true;
    _intake.steps[13].enabled = !!dossier.dossierId ;
    _intake.steps[13].isActive = !!dossier.dossierId ;
    _intake.steps[11].enabled = !!dossier.dossierId ;
    _intake.steps[11].isActive = !!dossier.dossierId ;
    _intake.steps[14].enabled = !!dossier.dossierId ;
    _intake.steps[14].isActive = !!dossier.dossierId ;
    _intake.steps[15].enabled = !!dossier.dossierId && viewCostControl ;
    _intake.steps[15].isActive = !!dossier.dossierId && viewCostControl ;

    if (
      dossier.policy != null &&
      dossier.policy.caseType.ctyHasAdl &&
      dossier.policy.caseType.ctyHasAdl == true &&
      !!dossier.dossierId
    ) {
      _intake.steps[3].enabled = true;
      _intake.steps[3].isActive = true;
    }
    if (
      dossier.policy != null &&
      dossier.policy.caseType.ctyHasRehospitalisation &&
      dossier.policy.caseType.ctyHasRehospitalisation == true &&
      !!dossier.dossierId
    ) {
      _intake.steps[4].enabled = true;
      _intake.steps[4].isActive = true;
    } else {
      _intake.steps[4].enabled = false;
      _intake.steps[4].isActive = false;
    }
    if (
      dossier.policy != null &&
      dossier.policy.caseType.ctyHasDbc &&
      dossier.policy.caseType.ctyHasDbc == true &&
      !!dossier.dossierId
    ) {
      _intake.steps[12].enabled = true;
      _intake.steps[12].isActive = true;
    } else {
      _intake.steps[12].enabled = false;
      _intake.steps[12].isActive = false;
    } 
    _intake.intake.sspCount = dossier.sspCount;
    ctx.setState({
      ...state,
      intake: _intake.intake,
      steps: _intake.steps,
    });
    this.router.navigate(["/dashboard/home/intake/summary"]);
  }

  costControlPermission(): boolean
  {
   return this.sharedService.checkPermissionByRole(FUNCTIONALITIES.COST_CONTROL_MANAGEMENT).hasRead || this.sharedService.checkPermissionByRole(FUNCTIONALITIES.COST_CONTROL_MANAGEMENT).hasEdit
  }

  getRoles(): boolean{ 
    let roles = this.authenticationService.getLocalStorageRoles();
    let groupRoles = [...new Set(roles.map(item => item.authorizationGroupRoleId))];
       return groupRoles.find(r=>r == ROLES.ADVANCED_MEDICAL_ASSISTANT || r == ROLES.COST_CONTROL || r == ROLES.ADMINISTRATORS) != undefined;
  }

  @Action(SetParty)
  setParty(ctx: StateContext<IntakeModel>, { party,editMode }) {
    const state = ctx.getState();
    let intake = JSON.parse(JSON.stringify(state.intake));
    if (party)
    intake.parties = [...party];
    let steps = JSON.parse(JSON.stringify(state.steps));
    let active = JSON.parse(JSON.stringify(state.active));
    if (state.active.url == "party") {
      steps = this.activeNextStep(state.steps, state.active).steps;
      active = this.activeNextStep(state.steps, state.active).active;
    }
    ctx.patchState({
      ...state,
      intake: intake,
      steps: steps,
      active: active,
    });
  }

  @Action(SetInsuree)
  setInsuree(ctx: StateContext<IntakeModel>, { insuree }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.insuree = insuree;
    if (intake.policy && intake.policy.personalInformations) {
      intake.policy.personalInformations.birthDate = insuree.ipaDateOfBirth;
    }
    let steps = JSON.parse(JSON.stringify(state.steps));
    let active = JSON.parse(JSON.stringify(state.active));
    if (state.active.url == "insuree") {
      steps = this.activeNextStep(state.steps, state.active).steps;
      active = this.activeNextStep(state.steps, state.active).active;
    }
    ctx.patchState({
      ...state,
      intake: intake,
      steps: steps,
      active: active,
    });
  }

  @Action(SetLinkedDossier)
  setLinkedDossier(ctx: StateContext<IntakeModel>, { linkedDossier }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.linkedDossiers = linkedDossier;
    ctx.patchState({
      ...state,
      intake: intake
    });
  }

  @Action(SetTravel)
  setTravel(ctx: StateContext<IntakeModel>, { travel }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.travel = travel;
    intake.travels = [travel];
    let steps = JSON.parse(JSON.stringify(state.steps));
    let active = JSON.parse(JSON.stringify(state.active));
    if (state.active.url == "travel") {
      steps = this.activeNextStep(state.steps, state.active).steps;
      active = this.activeNextStep(state.steps, state.active).active;
    }
    ctx.patchState({
      ...state,
      intake: intake,
      steps: steps,
      active: active,
    });
  }

  @Action(SetHelpPlan)
  setHelpPlan(ctx: StateContext<IntakeModel>, { helpPlans }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.helpPlans = helpPlans;
    ctx.patchState({
      ...state,
      intake: intake
    });
  }

  @Action(SetWorkingOrderLine)
  SetProforma(ctx: StateContext<IntakeModel>, { workingOrderLine, hpdId }) {
    const state = ctx.getState();
    let intake = state.intake;
    let _index= intake.helpPlans.findIndex( i => i.hpdId == hpdId);
    if(_index != -1){
      let _existedProformaIndex = intake.helpPlans[_index].proformaInvoiceList.findIndex(pi => pi.wolId == workingOrderLine.wolId)
      if(_existedProformaIndex == -1){
      intake.helpPlans[_index].proformaInvoiceList.push(workingOrderLine) ;
      }
      else{
        
      intake.helpPlans[_index].proformaInvoiceList[_existedProformaIndex] = workingOrderLine;
      }
    }
  
    ctx.patchState({
      ...state,
      intake: intake
    });
  }

  @Action(ValidateHelpPlan)
  valideHelpPlan(ctx: StateContext<IntakeModel>) {
    const state = ctx.getState();
    let steps = JSON.parse(JSON.stringify(state.steps));
    let active = JSON.parse(JSON.stringify(state.active));
    if (state.active.url == "help-plan-list") {
      steps = this.activeNextStep(state.steps, state.active).steps;
      active = this.activeNextStep(state.steps, state.active).active;
    }
    ctx.patchState({
      ...state,
      steps: steps,
      active: active
    });
  }

  @Action(SetPolicy)
  setPolicy(ctx: StateContext<IntakeModel>, { policyControl,editMode }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.policy = policyControl;
    ctx.patchState({
      ...state,
      intake: intake,
    });
  }
  @Action(SetActiveStepsPolicy)
  setActiveStepsPolicy(ctx: StateContext<IntakeModel>) 
  {
    const state = ctx.getState();

    let steps = JSON.parse(JSON.stringify(state.steps));
    let active = JSON.parse(JSON.stringify(state.active));
    if (state.active.url == "policy" || state.active.url == "location") {
      steps = this.activeNextStep(state.steps, state.active).steps;
      active = this.activeNextStep(state.steps, state.active).active;
    }
    ctx.patchState({
      ...state,
      active: active,
      steps: steps
    });
  }

  @Action(SetCaseType)
  setCaseType(ctx: StateContext<IntakeModel>, { caseType }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.policy.cause = caseType;
    intake.case = intake.cases.find(x=>x.caseDetailId == caseType.ctyCaseDetailId);
    intake.assignmentId = caseType.assignmentId;
    intake.travel = intake.travels.find(t=>t.tdAssignmentId == intake.assignmentId)
    ctx.patchState({
      ...state,
      intake: intake
    });

    this.router.navigate(["/dashboard/home/intake/summary"]);
  }

  @Action(SetCaseDetail)
  setCaseDetail(ctx: StateContext<IntakeModel>, { caseDetail }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.case = caseDetail;
    let steps = JSON.parse(JSON.stringify(state.steps));
    let active = JSON.parse(JSON.stringify(state.active));
    if (state.active.url == 'case') {
      steps = this.activeNextStep(state.steps, state.active).steps;
      active = this.activeNextStep(state.steps, state.active).active;
    }
    ctx.patchState({
      ...state,
      intake: intake,
      steps: steps,
      active: active
    });
  }


  @Action(SetStartIntakeTime)
  SetStartIntakeTime(ctx: StateContext<IntakeModel>, { intakeTimeValue }) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      intakeDuration: intakeTimeValue,
    });
  }

  @Action(SetDraftDossierNumber)
  SetDraftDossierNumber(ctx: StateContext<IntakeModel>, { draftDossierNumber }) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      draftDossierNumber: draftDossierNumber,
    });
  }

  @Action(SaveStore)
  SaveStore(ctx: StateContext<IntakeModel>, { }) {
    const state = ctx.getState();
    const intakeData = state.intake;
    const policyData = intakeData.policy;
    const insureeData = intakeData.insuree;
    let policyVm = plainToClass(PolicyVm, policyData);
    const policyDto = Mapper.map(policyVm, PolicyDto);
    let insureeVm = plainToClass(InsureeVm, insureeData);
    const insureeDto = Mapper.map(insureeVm, InsureeDto);
  }

  @Action(UpdateSetps)
  async updateSetps(ctx: StateContext<IntakeModel>, { caseType }) {
    const state = ctx.getState();    
    let intake = state.intake;
    if(intake.policy)
      intake.policy.cause = caseType;
    let _steps = this.getCaseTypeSteps(caseType, state);
    state.steps.map((step) => {
      step.isActive = step.label == "Policy";
    });

    ctx.setState({
      ...state,
      steps: _steps,
      active : _steps[0]
    });
  }

  @Action(SetPureCloudInfo)
  setPureCloudInfo(ctx: StateContext<IntakeModel>, { pureCloudInfo }) {
    const state = ctx.getState();
    let intake = state.intake; 
    
    intake.pureCloudInfo = pureCloudInfo;

    console.log('purecloudinfo2: ' + pureCloudInfo);

    ctx.patchState({      
      intake: intake,
    });
  }

  getCaseTypeSteps(caseType, state){
    const steps = JSON.parse(JSON.stringify(state.steps));
    let _steps = [];
    if (caseType) {
      _steps = steps.map((step) => {
        if (step.label == STEP.POLICY  && caseType.ctyHasPolicyStep != null)
          step.enabled = caseType.ctyHasPolicyStep;
        if (step.label == STEP.INSUREE && caseType.ctyHasInsureeStep != null)
          step.enabled = caseType.ctyHasInsureeStep;
        if (step.label == STEP.CASE && caseType.ctyHasCaseDetailsStep != null)
          step.enabled = caseType.ctyHasCaseDetailsStep;
        if (
          step.label == STEP.LOCATION &&
          caseType.ctyHasLocationStep != null
        )
          step.enabled = caseType.ctyHasLocationStep;
        if (step.label ==STEP.TRAVEL && ((caseType.ctyHasTravelStep !=null && caseType.ctyHasTravelStep !=undefined )
                                       || (caseType.ctyHasTravelDetails!=null && caseType.ctyHasTravelDetails != undefined)))
          step.enabled = caseType.ctyHasTravelStep || caseType.ctyHasTravelDetails;
        if (step.label == STEP.ADL && caseType.ctyHasAdl != null)
          step.enabled = caseType.ctyHasAdl && state.intake.dossierId;
        if (step.label == STEP.DBC && caseType.ctyHasDbc != null)
          step.enabled = caseType.ctyHasDbc && state.intake.dossierId;
        if (
          step.label == STEP.PARTY &&
          ((caseType.ctyHasFellowTravellersStep !=null && caseType.ctyHasFellowTravellersStep !=undefined) || (caseType.ctyHasFellowTravellers != null && caseType.ctyHasFellowTravellers !=undefined))
        )
          step.enabled = caseType.ctyHasFellowTravellersStep || caseType.ctyHasFellowTravellers;
        if (
          step.label == STEP.HELP_PLAN  &&
          caseType.ctyHasHelpPlansStep != null
        )
          step.enabled = caseType.ctyHasHelpPlansStep;
        if (step.label == STEP.SUMMARY)
          step.enabled = true;
        if (step.label == STEP.NOTES && caseType.ctyHasNoteStep != null)
          step.enabled = caseType.ctyHasNoteStep;

        if (step.label == STEP.CORRESPONDANCE)
          step.enabled = state.intake.dossierId;
        if(step.label == STEP.REHOSPITALISATION && caseType.ctyHasRehospitalisation != null)
          step.enabled =  state.intake.dossierId && caseType.ctyHasRehospitalisation;
        return step;
      });
    } else {
      let _intake = new IntakeModel();
      _steps = _intake.steps;
    }
    return _steps;

  }

  @Action(SetLocations)
  setLocations(ctx: StateContext<IntakeModel>, { locations }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.policy.cause.locations =locations;
    ctx.patchState({
      ...state,
      intake: intake
    });
  }

  @Action(SetIsUrgent)
  setIsUrgent(ctx: StateContext<IntakeModel>, { isUrgent }) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.isUrgent = isUrgent;

    ctx.patchState({
      ...state,
      intake: intake,
    });
  }

  private activeNextStep(steps: IntakeStepModel[], active: IntakeStepModel) {
    let i = 1;
    let index = -1;
    steps = JSON.parse(JSON.stringify(steps));
    let maxStep = steps.reduce((prev, curr) => (prev.step > curr.step) ? prev : curr, steps[0]).step;
    while (index < 0 && maxStep >= active.step + i) {
      index = steps.findIndex(step => step.step == active.step + i && step.enabled);
      i++;
    }
    if (index > -1) {
      steps[index].isActive = true;
    }
    let result = { steps: steps, active: steps[index] };
    return result;
  }


  generatCoroner(coroner): CoronerModel {
    let res = new CoronerModel();
    if (coroner != null) {
      res.corName = coroner.name;
      res.corCity = coroner.city;
      res.corHouseNumber = coroner.houseNumber;
      res.corStreetName = coroner.streetName;
      res.corHouseNumberAddition = coroner.houseNumberAddition;
      res.corContactPerson = coroner.contactPerson;
      res.corCountry = coroner.countryId;
      res.corPhoneNumber = coroner.phoneNumber;
      res.corPostalCode = coroner.postalCode;
      return res;
    }
    else return null;
  }

  @Action(SetInsureeData)
  setInsureeData(ctx: StateContext<IntakeModel>, {insureeData}) {
    const state = ctx.getState();
    let intake = state.intake;
    intake.insureeData = insureeData;
    ctx.patchState({
      ...state,
      intake: intake,
    });
  }


}